import {
  APIFieldItem,
  FieldDataSource,
  SettingsFieldType
} from "@/pages/settings/types";
import {
  OnboardingStageWorkflowItem,
  OnboardingWorkflowItemInput,
  OnboardingWorkflowItemResponseType,
  OnboardingWorkflowItemUpdateInput
} from "@/pages/settings/components/contractsSettings/types";
import { buildFieldConfig } from "@/pages/settings/components/dealsSettings/FieldManagement/utils/fieldFormUtils";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import { QuestionnaireType } from "@/components/controls/Questionnaires/Questionnaire";
import type { DreamerFlow } from "@/pages/settings/components/dealsSettings/StageStudio/workflow/hooks/useAutofillApi";

export interface WorkflowFieldFormData {
  id?: string;
  fieldLabel?: string;
  fieldType?: string;
  fieldHelpText?: string | null;
  isRequiredForPass?: boolean;
  filledByDreamer?: boolean;
  dreamerFlow?: DreamerFlow | null;
  // The draft item the autofill section generated instructions against, if any.
  autofillQuestionId?: string | null;
  options?: string[];
  allowUsersToAdd?: boolean;
  defaultValue?: string;
  option1?: string;
  option2?: string;
  minValue?: number;
  maxValue?: number;
  checkboxInlineText?: string;
  config?: Record<string, unknown> | null;
}

const buildOnboardingResponseType = (
  fieldType: string | undefined,
  config: Record<string, unknown> | null
): OnboardingWorkflowItemResponseType => ({
  type: fieldType || "text",
  value: (config?.defaultValue as string) ?? "none",
  setValueType: null,
  unit: null,
  ...(config ?? {})
});

const ONBOARDING_WORKFLOW_TYPE_BY_VALUE: Record<number, QuestionnaireType> = {
  1: QuestionnaireType.GENERAL,
  2: QuestionnaireType.SIMPLE
};

export const mapOnboardingWorkflowType = (
  workflowType: number | null | undefined
): QuestionnaireType =>
  ONBOARDING_WORKFLOW_TYPE_BY_VALUE[workflowType ?? 1] ??
  QuestionnaireType.GENERAL;

const extractResponseTypeString = (
  responseType: OnboardingStageWorkflowItem["responseType"]
): string => {
  if (typeof responseType === "string") return responseType;
  if (responseType && typeof responseType === "object") {
    const obj = responseType as { type?: unknown };
    if (typeof obj.type === "string") return obj.type;
  }
  return "text";
};

const resolveOnboardingDataSource = (
  item: OnboardingStageWorkflowItem
): FieldDataSource => {
  if (!item.filledByDreamer) return FieldDataSource.MANUAL;
  return item.isManual ? FieldDataSource.HYBRID : FieldDataSource.DREAMER;
};

// Drafts exist only while a drawer generates Dreamer instructions (or were left behind by
// one that never saved), so they are not fields of the workflow.
export const isPublishedOnboardingItem = (
  item: OnboardingStageWorkflowItem
): boolean => !item.isDraft;

export const transformOnboardingItemsToFields = (
  items: OnboardingStageWorkflowItem[]
): APIFieldItem[] =>
  items.map((item, index) => ({
    id: item.id,
    fieldKey: item.id,
    label: item.name,
    displayType: extractResponseTypeString(
      item.responseType
    ) as APIFieldItem["displayType"],
    fieldType: SettingsFieldType.CUSTOM,
    dataSource: resolveOnboardingDataSource(item),
    order: item.itemOrder ?? index + 1,
    filledByDreamer: item.filledByDreamer ?? false,
    dreamerFlow: item.dreamerFlow ?? null,
    showInCreation: false,
    showInDetailedView: true,
    required: item.isGuardrails ?? false,
    config:
      item.responseType && typeof item.responseType === "object"
        ? item.responseType
        : null,
    helpText: item.tooltipText ?? null,
    section: null,
    fieldManagement: {
      canEditDefinition: true,
      canEditOptions: true,
      canDelete: true,
      canHide: false
    }
  }));

export const mapFieldDataToCreateOnboardingItem = (
  fieldData: WorkflowFieldFormData,
  workflowId: string
): OnboardingWorkflowItemInput => {
  const config = buildFieldConfig(
    {
      fieldType: fieldData.fieldType as FieldDisplayType,
      fieldLabel: fieldData.fieldLabel ?? "",
      fieldHelpText: fieldData.fieldHelpText ?? "",
      option1: fieldData.option1,
      option2: fieldData.option2,
      minValue: fieldData.minValue,
      maxValue: fieldData.maxValue,
      checkboxInlineText: fieldData.checkboxInlineText
    },
    {
      options: fieldData.options ?? [],
      allowUsersToAdd: fieldData.allowUsersToAdd ?? false,
      defaultValue: fieldData.defaultValue ?? "",
      checkboxInlineText: fieldData.checkboxInlineText
    },
    true
  );

  return {
    contractWorkflowId: workflowId,
    name: fieldData.fieldLabel || "",
    responseType: buildOnboardingResponseType(fieldData.fieldType, config),
    isGuardrails: fieldData.isRequiredForPass ?? false,
    isManual: !(fieldData.filledByDreamer ?? false),
    filledByDreamer: fieldData.filledByDreamer ?? false,
    tooltipText: fieldData.fieldHelpText ?? null
  };
};

export const mapFieldDataToPublishOnboardingDraft = (
  fieldData: WorkflowFieldFormData & { dreamerFlow: DreamerFlow },
  workflowId: string
): OnboardingWorkflowItemUpdateInput => {
  const { contractWorkflowId: _workflowId, ...item } =
    mapFieldDataToCreateOnboardingItem(fieldData, workflowId);
  void _workflowId;
  return {
    ...item,
    isManual: false,
    filledByDreamer: true,
    dreamerFlow: fieldData.dreamerFlow,
    isDraft: false
  };
};

// `includeAutofill` sends the flow alongside the Dreamer switch. Off while the autofill
// section is hidden, where the drawer holds no flow and sending one would erase it.
export const mapFieldDataToUpdateOnboardingItem = (
  fieldData: WorkflowFieldFormData,
  includeAutofill = false
): OnboardingWorkflowItemUpdateInput => {
  const update: OnboardingWorkflowItemUpdateInput = {};
  if (fieldData.fieldLabel !== undefined) update.name = fieldData.fieldLabel;
  if (fieldData.fieldType !== undefined)
    update.responseType = buildOnboardingResponseType(
      fieldData.fieldType,
      fieldData.config ?? null
    );
  if (fieldData.isRequiredForPass !== undefined)
    update.isGuardrails = fieldData.isRequiredForPass;
  if (fieldData.filledByDreamer !== undefined) {
    update.filledByDreamer = fieldData.filledByDreamer;
    update.isManual = !fieldData.filledByDreamer;
    // Switching off clears the flow; a null flow from the drawer clears it too, only undefined keeps it.
    if (includeAutofill && !fieldData.filledByDreamer) {
      update.dreamerFlow = null;
    } else if (includeAutofill && fieldData.dreamerFlow !== undefined) {
      update.dreamerFlow = fieldData.dreamerFlow;
    }
  }
  if (fieldData.fieldHelpText !== undefined)
    update.tooltipText = fieldData.fieldHelpText;
  return update;
};
